import { createSelector } from 'reselect';

import type { State } from '../../Types';
import { LoadStatus } from '../../Types';
import type { InvoicesState } from './Types';
import type { InvoiceOccupant, InvoiceProperty } from './Types/InvoiceProperty';

const invoicesState = ({ invoices }: State) => invoices;

export const getInvoicesFeatureStatus = createSelector(invoicesState, ({ enabled }: InvoicesState) => enabled);

// Properties
const propertiesSlice = createSelector(invoicesState, ({ properties }) => properties);

export const propertiesLoadStatus = createSelector(propertiesSlice, ({ loadStatus }) => loadStatus);

export const propertiesAreLoaded = createSelector(propertiesLoadStatus, (loadStatus) => loadStatus === LoadStatus.LOADED);

export const sortedPropertiesList = createSelector(
    invoicesState,
    ({ properties: propertiesState }): InvoiceProperty[] => propertiesState.sortOrder?.map((i) => propertiesState[i]) || [],
);

export const propertiesWithPaymentProvider = createSelector(sortedPropertiesList, (propertiesListState): InvoiceProperty[] =>
    propertiesListState.filter((property) => property.company?.paymentProviderIde),
);

export const occupantsWithPaymentProvider = createSelector(propertiesWithPaymentProvider, (propertiesListState) =>
    propertiesListState.flatMap((property) =>
        property.occupants.map((occupant) => ({
            id: occupant.id,
            name: occupant.name,
            propertyId: property.id,
            propertyName: property.name,
        })),
    ),
);

export const property = (propertyId: number): ReturnType<typeof createSelector> =>
    createSelector(propertiesSlice, (propertiesState): InvoiceProperty => propertiesState[propertyId] || {});

// Occupants
export const propertyOccupants = (propertyId: number): ReturnType<typeof createSelector> =>
    createSelector(propertiesSlice, (propertiesState): InvoiceOccupant[] => propertiesState[propertyId]?.occupants || []);

export const occupant = (propertyId: number, occupantId: number): ReturnType<typeof createSelector> =>
    createSelector(propertiesSlice, (propertiesState): InvoiceOccupant | undefined =>
        Object.prototype.hasOwnProperty.call(propertiesState, propertyId) &&
        propertiesState[propertyId].occupants.some((o) => o.id === occupantId)
            ? propertiesState[propertyId].occupants.filter((o) => o.id === occupantId)[0]
            : undefined,
    );
